import { Flex } from "@radix-ui/themes";
import { Step } from "@/v2/features/sampling/Step";
import { EscalationRuleFormData } from "../EscalationRuleForm";
import Select, { SelectOption } from "@/components/shared/select";
import { EscalationStrategy } from "@/v2/entities/release/Release.model";
import { useEscalationRuleApi } from "@/v2/entities/escalation-rule/EscalationRule.api";
import { useQuery } from "react-query";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useMemo } from "react";

interface StrategyStepProps {
  value: EscalationRuleFormData;
  simulatorId: string | undefined;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  allowLiveAgent?: boolean;
}

export function StrategyStep({
  value,
  simulatorId,
  onChange,
  allowLiveAgent = true,
}: StrategyStepProps) {
  const { getLiveAgentAvailability } = useEscalationRuleApi(simulatorId);
  const { workspace } = useWorkspace();

  const { data: liveAgentData } = useQuery({
    queryKey: [
      "live-agent-availability",
      workspace,
      simulatorId,
      allowLiveAgent,
    ],
    queryFn: () => getLiveAgentAvailability(workspace),
    enabled: Boolean(workspace) && allowLiveAgent,
  });

  const strategies = useMemo<SelectOption[]>(
    () => [
      {
        label: "Send Automated Message",
        value: EscalationStrategy.NONE,
      },
      {
        label: "Create Support Ticket",
        value: EscalationStrategy.CREATE_TICKET,
      },
      ...(liveAgentData === true && allowLiveAgent
        ? [
            {
              label: "Escalate to Live Agent",
              value: EscalationStrategy.LIVE_AGENT,
            },
          ]
        : []),
    ],
    [liveAgentData, allowLiveAgent]
  );

  return (
    <Step index={1} label="Strategy" description="Choose escalation strategy">
      <Flex
        direction={"column"}
        gap={"4"}
        style={{ width: "400px", padding: "4px" }}
      >
        <Select
          options={strategies}
          value={value.escalationStrategy}
          onChange={(value) =>
            onChange({ escalationStrategy: value as EscalationStrategy })
          }
        />
      </Flex>
    </Step>
  );
}
